
import React from 'react';
import { Progress } from '@/components/ui/progress';
import GlassPill, { GlassPillVariant } from '@/components/ui/GlassPill';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export type TaskStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

export interface Task {
  name: string;
  status: TaskStatus;
}

interface LoadingProgressProps {
  tasks: Task[];
  progress: number;
  currentTaskDescription: string;
}

const statusMap: Record<TaskStatus, { icon: React.ReactNode; variant: GlassPillVariant; text: string }> = {
  'pending': { icon: <Clock className="h-3 w-3" />, variant: 'gray', text: 'Pending' },
  'in-progress': { icon: <Loader2 className="h-3 w-3 animate-spin" />, variant: 'blue', text: 'In Progress' },
  'completed': { icon: <CheckCircle className="h-3 w-3" />, variant: 'green', text: 'Completed' },
  'failed': { icon: <XCircle className="h-3 w-3" />, variant: 'red', text: 'Failed' },
};

const LoadingProgress: React.FC<LoadingProgressProps> = ({ tasks, progress, currentTaskDescription }) => {
  return (
    <div className="space-y-6 text-center">
      <h3 className="text-lg font-medium text-foreground">{currentTaskDescription}</h3>
      <Progress value={progress} className="w-full" indicatorClassName="bg-purple-500" />
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Task Breakdown</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tasks.map((task, index) => (
            <motion.div
              key={task.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-3 rounded-lg border bg-background/50"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{task.name}</span>
                <GlassPill 
                  type={statusMap[task.status].text} 
                  variant={statusMap[task.status].variant}
                  icon={statusMap[task.status].icon}
                >
                  {statusMap[task.status].text}
                </GlassPill>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingProgress;
